import { useEffect, type ComponentProps } from 'react';
import { Animated } from 'react-native';
import type { StackNavigationOptions } from '@react-navigation/stack';
import { createTheme as createDSTheme } from 'app/shopify';
import type { ScreenOptionsNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import { createScreenOptions } from '../screen-options';
import { RIGHT_PANEL_WIDTH } from '../dimensions';
import { isMobileNative } from 'app/utils/constants';
import { getWebWindow } from 'app/utils/web-window';

const theme = createDSTheme();

function WebRightPanelOverlay({
  route,
  navigation,
  style,
}: {
  route: SpecificRouteProp;
  navigation: ScreenOptionsNavigationProp;
  style: ComponentProps<typeof Animated.View>['style'];
}) {
  const state = navigation.getState();
  const currentRoute = state.routes[state.index];
  const window = getWebWindow();
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onPointerDown = (event: PointerEvent) => {
      if (!window) return;
      const width = Math.min(RIGHT_PANEL_WIDTH, window.innerWidth);
      const left = window.innerWidth - width;
      if (event.clientX < left && currentRoute?.name === 'notificationsOverlay') {
        navigation.goBack();
      }
    };

    if (isMobileNative) return;
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [currentRoute?.name, navigation, route.name, route.path, window]);

  return (
    <Animated.View pointerEvents="none" style={[{ position: 'absolute' as const, backgroundColor: '#000' }, style]} />
  );
}

export function createWebRightPanelScreenOptions(
  navigation: ScreenOptionsNavigationProp,
  route: SpecificRouteProp,
): StackNavigationOptions {
  const screenOpts = createScreenOptions(route) as StackNavigationOptions;
  const width = Math.min(RIGHT_PANEL_WIDTH, getWebWindow()?.innerWidth ?? 0);

  return {
    ...screenOpts,
    header: undefined,
    headerShown: false,
    presentation: 'transparentModal' as const,
    cardOverlayEnabled: true,
    cardStyle: {
      alignSelf: 'flex-end' as const,
      backgroundColor: theme.colors['surface.default'],
      borderTopLeftRadius: theme.borderRadii['radius.sm'],
      boxShadow: '-12px 0 32px rgba(0, 0, 0, 0.14)',
      flex: 1,
      height: '100%' as const,
      maxHeight: '100%' as const,
      overflow: 'hidden' as const,
      width,
    },
    cardStyleInterpolator: ({ current: { progress } }) => ({
      cardStyle: {
        transform: [
          {
            translateX: progress.interpolate({
              inputRange: [0, 1],
              outputRange: [width, 0],
            }),
          },
        ],
      },
      overlayStyle: {
        opacity: progress.interpolate({
          inputRange: [0, 1],
          outputRange: [0, 0.18],
        }),
      },
    }),
    cardOverlay: ({ style }) => <WebRightPanelOverlay route={route} navigation={navigation} style={style} />,
  };
}
